import { useEffect, useState } from 'react';
import { useLingui } from '@lingui/react';
import { BellRing, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useAuthContext } from '@/contexts/AuthContext';
import { useEmployeePickerNames } from '@/hooks/useEmployees';
import { ApiError } from '@/api';
import { useRemindWorkflowItem } from '@/hooks/useWorkflows';
import type { WorkflowItemResponse } from '@/types/workflow';
import { responsibleFor, responsibleName } from '@/types/workflowResponsible';
import { returnFocusOnClose } from './returnFocus';

/** The slice of an item the dialog needs. */
export type RemindDialogItem = Pick<
  WorkflowItemResponse,
  | 'id'
  | 'category'
  | 'admin_only'
  | 'assigned_to'
  | 'assigned_role'
  | 'group_lead_employee_id'
  | 'group_lead_name'
  | 'group_lead_clerk_user_id'
  | 'group_name'
>;

interface RemindDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  item: RemindDialogItem | null;
  onSuccess?: () => void;
  /** Gets the focus back when the dialog closes: the element that opened it. */
  returnFocusTo?: HTMLElement | null;
}

/**
 * Send a push reminder to whoever holds the item: the person, else the group
 * lead. With neither there is nobody to remind and the dialog says so.
 */
export function RemindDialog({ open, onOpenChange, item, onSuccess, returnFocusTo }: RemindDialogProps) {
  const { i18n } = useLingui();
  const { userId } = useAuthContext();
  const { namesByClerkUserId } = useEmployeePickerNames();
  const remind = useRemindWorkflowItem();
  const [comment, setComment] = useState('');
  // The server refused: this person was reminded in the last hour.
  const [tooSoon, setTooSoon] = useState(false);

  useEffect(() => {
    if (open) {
      setComment('');
      setTooSoon(false);
    }
  }, [open, item?.id]);

  if (!item) return null;

  // Same order as the server: the person who holds it, else the group lead,
  // who is never the fallback on an admin-only item (they could not open it).
  const hasHolder = !!item.assigned_to;
  const hasLead = !!item.group_lead_clerk_user_id && !item.admin_only;
  const recipientUserId = hasHolder ? item.assigned_to : hasLead ? item.group_lead_clerk_user_id : undefined;
  // The server refuses a reminder to the caller (a lead reminding their own group).
  const remindsSelf = !!recipientUserId && !!userId && recipientUserId === userId;
  const canRemind = !!recipientUserId && !remindsSelf;
  const recipient = hasHolder
    ? responsibleName(
        i18n,
        responsibleFor(
          { ...item, group_lead_employee_id: undefined },
          { userId, namesByClerkUserId, unknownName: i18n._({ id: 'common.unknown', message: 'Unknown' }) },
        ),
      )
    : (item.group_lead_name ?? '');

  const submit = () => {
    if (!canRemind || tooSoon || remind.isPending) return;
    remind.mutate(
      { id: item.id ?? '', comment: comment.trim() || undefined },
      {
        onSuccess: () => {
          onOpenChange(false);
          onSuccess?.();
        },
        onError: (error: Error) => {
          if (isReminderTooSoon(error)) setTooSoon(true);
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="remind-dialog" onCloseAutoFocus={returnFocusOnClose(returnFocusTo)}>
        <DialogHeader>
          <DialogTitle>{i18n._({ id: 'workflow.remind.title', message: 'Send a reminder' })}</DialogTitle>
          <DialogDescription>
            {i18n._({
              id: 'workflow.remind.description',
              message: 'The person gets a push notification.',
            })}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {canRemind ? (
            <p className="text-sm" data-testid="remind-recipient">
              {i18n._({ id: 'workflow.remind.recipient', message: 'Reminder goes to {name}', values: { name: recipient } })}
            </p>
          ) : remindsSelf ? (
            <p className="text-sm text-muted-foreground" data-testid="remind-self">
              {i18n._({ id: 'workflow.remind.self', message: 'The reminder would go to you.' })}
            </p>
          ) : (
            <p className="text-sm text-muted-foreground" data-testid="remind-nobody">
              {i18n._({
                id: 'workflow.remind.nobody',
                message: 'No one has taken this yet, and no group lead has a login. There is nobody to remind.',
              })}
            </p>
          )}

          {canRemind && tooSoon && (
            <p className="text-sm text-destructive" role="alert" data-testid="remind-too-soon">
              {i18n._({ id: 'workflow.remind.error.tooSoon', message: 'Already reminded in the last hour' })}
            </p>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="remind-comment">
              {i18n._({ id: 'workflow.remind.comment', message: 'Note (optional)' })}
            </Label>
            <Textarea
              id="remind-comment"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={2}
              maxLength={10000}
              disabled={!canRemind}
              placeholder={i18n._({ id: 'workflow.remind.commentPlaceholder', message: 'What to look at, or why it is urgent' })}
            />
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={remind.isPending}>
            {i18n._({ id: 'common.cancel', message: 'Cancel' })}
          </Button>
          <Button type="button" disabled={!canRemind || tooSoon || remind.isPending} onClick={submit} data-testid="remind-confirm">
            {remind.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
            ) : (
              <BellRing className="mr-2 h-4 w-4" aria-hidden="true" />
            )}
            {i18n._({ id: 'workflow.remind.confirm', message: 'Send reminder' })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** The server's "reminded in the last hour" refusal (`WORKFLOW_REMINDER_TOO_SOON`, a 422). */
function isReminderTooSoon(error: Error): boolean {
  if (!(error instanceof ApiError)) return false;
  const body: unknown = error.body;
  return typeof body === 'object' && body !== null && (body as { code?: unknown }).code === 'WORKFLOW_REMINDER_TOO_SOON';
}
